import { Box } from '@mui/material';
import type { Router } from 'app/app-state';
import Loading from 'app/common/components/loading';
import Messages from 'app/messages/components/messages';
import DelinquencyBanner from 'app/project/components/delinquency-banner';
import DocumentTitle from 'app/project/components/document-title';
import type React from 'react';
import styled from 'styled-components';
import { CustomError } from '../common/components/errors';
import { maintenance } from '../config';
import { ErrorBoundary } from './error-boundary';
import { GlobalModals } from './global-modals';
import { MaintenanceMessage } from './maintenance/maintenance-message';
import DefaultTopBar from './topbar';

export const AppContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background-color: ${({ theme }): string => theme.palette.grey['100']};
`;

export const LayoutContainer = styled(Box)`
  flex: 1 1 auto;
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 0;
  margin-top: 48px;
`;

export const NavContainer = styled(Box)`
  flex: 0 0 13.75rem;
  height: 100%;
  overflow-y: scroll;
  &:empty {
    display: none;
  }
`;

export const DefaultMainContainer = styled(Box)`
  flex: 1 1 auto;
  height: 100%;
  overflow-y: scroll;
`;

export const StaticMainContainer = styled(Box)`
  flex: 1 1 auto;
  overflow: hidden;
`;

export type AppLayoutProps = {
  readonly router: Router;
  readonly projectLoaded: boolean;
  readonly error?: string;
};

/**
 * E.g., "/project/:projectId/branch/:branch/api" -> branch-branch-api
 * "/account-settings" -> account-settings
 */
export function routeToSectionName(route: string) {
  route = route.replace('/project/:projectId', '');
  return route
    .replace(/[^A-Za-z]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-/, '')
    .replace(/-$/, '');
}

export function AppLayout(props: AppLayoutProps): JSX.Element {
  const { router, projectLoaded, error } = props;
  const TopBar = router?.result?.components?.topBar !== undefined ? router?.result?.components?.topBar : DefaultTopBar;
  const Main = router?.result?.components?.main;
  const Nav = router?.result?.components?.nav;
  const staticLayout = router?.result?.components?.staticLayout;
  const sectionName = router?.result?.components?.sectionName ?? routeToSectionName(router.route);

  const MainContainer = staticLayout ? StaticMainContainer : DefaultMainContainer;

  // We need to wait for the schema to load into state
  // and we do this by waiting for the project to load
  const isLoading = Boolean(router?.route.includes('/project/')) && !projectLoaded;

  let content: React.ReactNode;
  if (error) {
    content = <CustomError message={error} />;
  } else if (isLoading) {
    content = <Loading />;
  } else {
    content = (
      <ErrorBoundary pathname={router.pathname}>
        {Nav && (
          <NavContainer>
            <Nav router={router} />
          </NavContainer>
        )}
        {Main ? (
          <MainContainer data-testid={sectionName ? `main--name-${sectionName}` : undefined}>
            <Main router={router} />
          </MainContainer>
        ) : (
          <Loading />
        )}
        <Messages />
        <GlobalModals />
      </ErrorBoundary>
    );
  }

  return (
    <AppContainer>
      <DocumentTitle />
      {TopBar && maintenance?.enabled ? (
        <MaintenanceMessage
          message={maintenance.message}
          backgroundColor={maintenance.backgroundColor}
          dismissible={maintenance.dismissible}
        />
      ) : null}
      {TopBar && <TopBar />}
      <DelinquencyBanner />
      <LayoutContainer>{content}</LayoutContainer>
    </AppContainer>
  );
}
